import { Outlet, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Layout } from '../Layout';
import { ThemeProvider } from '../../contexts/ThemeContext';
import { useEffect, useState } from 'react';
import { setAuthTokenProvider, setCompanyIdProvider } from '../../services/api/ApiService';
import { setCurrentUserForActivities } from '../../hooks/useActivityTracking';
import { useAuthStore } from '../../stores/authStore';
import { useAuditorStore } from '../../stores/auditorStore';
import { Loader2 } from 'lucide-react';
import { isAuditRole } from '../../utils/roles';

export function ProtectedRoute() {
  const { isAuthenticated, isLoading: authLoading, loginWithRedirect, getAccessTokenSilently, user } = useAuth0();
  const { initialize, loading: authStoreLoading, syncStatus, organization, user: authStoreUser } = useAuthStore();
  const { activeCompany } = useAuditorStore();
  const [isInitialized, setIsInitialized] = useState(false);

  const isAuditor = isAuditRole(authStoreUser?.role);

  // Configurer le provider de token pour les appels API
  useEffect(() => {
    setAuthTokenProvider(async () => {
      try {
        if (isAuthenticated) {
          const token = await getAccessTokenSilently();
          return token;
        }
        return null;
      } catch (error) {
        console.error('Error getting access token', error);
        return null;
      }
    });

    // Provider pour X-Company-Id (auditeurs avec entreprise active)
    setCompanyIdProvider(() => {
      const { user: u, organization: org } = useAuthStore.getState();
      if (isAuditRole(u?.role) && org?.id) return org.id;
      return null;
    });
  }, [getAccessTokenSilently, isAuthenticated]);

  // Initialiser authStore après authentification Auth0
  useEffect(() => {
    const initializeAuthStore = async () => {
      if (isAuthenticated && !isInitialized) {
        console.log('🔄 Initialisation du contexte utilisateur via /auth/me...');
        await initialize();
        setIsInitialized(true);
        console.log('✅ Contexte utilisateur initialisé');
      }
    };
    
    initializeAuthStore();
  }, [isAuthenticated, initialize, isInitialized]);

  // Configurer l'utilisateur pour le tracking des activités
  useEffect(() => {
    if (isAuthenticated && user) {
      const displayName = user.name || user.nickname || user.email || 'Utilisateur';
      setCurrentUserForActivities(displayName);
    }
  }, [isAuthenticated, user]);

  // Afficher le loader pendant l'authentification Auth0
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  // Rediriger vers login si non authentifié
  if (!isAuthenticated) {
    loginWithRedirect();
    return null;
  }

  // Afficher le loader pendant l'initialisation du contexte utilisateur
  if (authStoreLoading || !isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Chargement du contexte...</p>
        </div>
      </div>
    );
  }

  // Afficher un message si l'organisation est en cours de synchronisation
  if (syncStatus === 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center max-w-md">
          <Loader2 className="w-10 h-10 animate-spin text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Synchronisation en cours
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Votre organisation est en cours de configuration. 
            Veuillez patienter quelques instants...
          </p>
        </div>
      </div>
    );
  }

  // ---- Auditors: redirect to client list if no active company ----
  if (isAuditor && !activeCompany) {
    return <Navigate to="/auditor/clients" replace />;
  }

  // Auditor with active company → use standard Layout (full company experience:
  // fiscal years, hierarchy, chat, etc. — same as regular company users)
  if (isAuditor && activeCompany) {
    return (
      <ThemeProvider>
        <Layout>
          <Outlet />
        </Layout>
      </ThemeProvider>
    );
  }

  // Bloquer l'accès si l'organisation est absente (organization: null depuis /auth/me)
  // On vérifie organization.id ET user.organizationId (les deux champs de la réponse /auth/me)
  // Couvre les accès directs par URL, refresh, ou liens partagés
  const hasOrganization = organization?.id || authStoreUser?.organizationId;
  if (!hasOrganization) {
    return <Navigate to="/no-organization" replace />;
  }

  return (
    <ThemeProvider>
      <Layout>
        <Outlet />
      </Layout>
    </ThemeProvider>
  );
}